import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Avatar,
  Box,
  Stack,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NameLogo from "../assets/Name_logo.png";
import { signOut } from "firebase/auth";
import { auth } from "../firebase"; // adjust path if needed
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

function Navbar({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // breakpoint for responsiveness

  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const navItems = [
    { label: "Home", href: "/" },
    { label: "BioThread", href: "/biothread/discover" },
    { label: "Article Miner", href: "/article" },
    { label: "SeqIntel", href: "/seqintel/nucleoread" },
    { label: "Support", href: "/support" },
  ];

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 20,
        left: "50%",
        transform: "translateX(-50%)",
        width: { xs: "95%", md: "85%" },
        borderRadius: 50,
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        zIndex: 1000,
        px: { xs: 2, md: 4 },
        py: 1,
        // background: "linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {/* Logo */}
        <Box
          sx={{ height: 60, width: 110, display: "flex", alignItems: "center" }}
        >
          <Box
            component="img"
            src={NameLogo}
            alt="ROBIO Logo"
            sx={{
              width: "100%",
              height: "auto",
              transform: "scale(1.2)",
              cursor: "pointer",
              transition: "transform 0.3s ease",
              "&:hover": { transform: "scale(1.3)" },
            }}
            onClick={() => navigate("/")}
          />
        </Box>

        {/* Desktop Nav */}
        {!isMobile && (
          <Stack direction="row" spacing={3} alignItems="center">
            {navItems.map((item) => {
              const isActive =
                location.pathname === item.href ||
                location.pathname.startsWith(item.href + "/");
              return (
                <Button
                  key={item.label}
                  variant="text"
                  onClick={() => navigate(item.href)}
                  sx={{
                    textTransform: "none",
                    fontWeight: isActive ? 700 : 500,
                    fontSize: "0.95rem",
                    color: "#1e3a8a",
                    position: "relative",
                    "&:after": {
                      content: '""',
                      position: "absolute",
                      width: isActive ? "100%" : "0%",
                      height: "2px",
                      bottom: 0,
                      left: 0,
                      backgroundColor: "#1e3a8a",
                      transition: "width 0.3s",
                    },
                    "&:hover:after": {
                      width: "100%",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}

            {/* User / Auth */}
            {user ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="span"
                  sx={{ color: "#1e3a8a", fontSize: "0.9rem", fontWeight: 500 }}
                >
                  {user.displayName || "User"}
                </Box>
                <Button
                  onClick={handleLogout}
                  sx={{
                    minWidth: "auto",
                    p: 0.5,
                    color: "#1e3a8a",
                    "&:hover": { bgcolor: "rgba(30,58,138,0.1)" },
                  }}
                >
                  <ArrowForwardIcon />
                </Button>
              </Stack>
            ) : (
              <>
                <Button
                  variant="text"
                  onClick={() => navigate("/login")}
                  sx={{
                    color: "#1e3a8a",
                    fontWeight: 500,
                    "&:hover": { bgcolor: "rgba(30,58,138,0.1)" },
                  }}
                >
                  Login
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate("/signup")}
                  sx={{
                    background: "linear-gradient(90deg, #1e3a8a, #3b82f6)",
                    borderRadius: "20px",
                    px: 3,
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#0f2454" },
                  }}
                >
                  Sign Up
                </Button>
              </>
            )}
          </Stack>
        )}

        {/* Hamburger Menu for Mobile */}
        {isMobile && (
          <IconButton onClick={toggleDrawer(true)} color="primary">
            <MenuIcon />
          </IconButton>
        )}
      </Stack>

      {/* Drawer for mobile menu */}
      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{ width: 250, p: 2 }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            {navItems.map((item) => {
              const isActive =
                location.pathname === item.href ||
                location.pathname.startsWith(item.href + "/");
              return (
                <ListItem
                  button
                  key={item.label}
                  onClick={() => {
                    navigate(item.href);
                  }}
                >
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 700 : 400,
                      color: "#1e3a8a",
                    }}
                  />
                </ListItem>
              );
            })}

            {/* User / Auth in drawer */}
            {user ? (
              <ListItem
                button
                onClick={() => {
                  handleLogout();
                }}
              >
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{ color: "#1e3a8a" }}
                />
              </ListItem>
            ) : (
              <>
                <ListItem
                  button
                  onClick={() => {
                    navigate("/login");
                  }}
                >
                  <ListItemText
                    primary="Login"
                    primaryTypographyProps={{ color: "#1e3a8a" }}
                  />
                </ListItem>
                <ListItem
                  button
                  onClick={() => {
                    navigate("/signup");
                  }}
                >
                  <ListItemText
                    primary="Sign Up"
                    primaryTypographyProps={{ color: "#1e3a8a" }}
                  />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}

export default Navbar;
